<ng-container *ngIf="shouldShowChart">
	<h3>
		<span class="icon-caption title-main" dcrContent dcrHTMLKey="PERFORMANCE_CHART_HEADER">Growth of Hypothetical Investment</span>
		<icon type="info-circle" *ngIf="tooltipText" class="info-circle text-14 color-primary" [tooltip]="infoBubble"> </icon>
	</h3>
	<ng-template #infoBubble> <section [innerHTML]="tooltipText"></section> </ng-template>
	<label *ngIf="asOfDate">
		<h3 class="text-16 margin-standard table-as-of-date" data-cy="hypothetical-performance-as-of-date">As of {{ asOfDate }}</h3>
	</label>
	<div class="row hypothetical-input-row">
		<div class="col-4">
			<p class="text-12 hypo-amount-label" dcrHTMLKey="HYPOTHETICAL_GROWTH_AMOUNT_LABEL">Enter hypothetical amount</p>
			<div class="text-16 hypothetical-input text-align-left">
				<div id="currency" class="text-16-bold" [innerHTML]="displayCurrency"></div>
				<input
					type="text"
					currency-input
					[symbolDisplay]="false"
					[digitDisplay]="'.0'"
					[ngModel]="hypotheticalAmountTxt"
					(ngModelChange)="onChangeHypoAmount($event)"
					(keyup)="onKeyUp($event)"
					data-cy="hypothetical-performance-amount-input"
				/>
			</div>
		</div>
		<ng-container *ngIf="enableHypoChartPeriodChange && earliestDataPoint">
			<div class="col-4" [dcrKeys]="['HYPO_SELECT_START_MONTH_LABEL', 'HYPO_SELECT_START_MONTH_PLACEHOLDER']">
				<dz-select
					[label]="'Select Start Month' | dcr : 'HYPO_SELECT_START_MONTH_LABEL'"
					[placeholder]="'Select a month' | dcr : 'HYPO_SELECT_START_MONTH_PLACEHOLDER'"
					[(value)]="startMonthValue"
					(valueChange)="this.isMonthUnselected = false"
				>
					<dz-option *ngFor="let month of startMonthOptions" [value]="month">{{ month }}</dz-option>
				</dz-select>
			</div>
			<div class="col-4" [dcrKeys]="['HYPO_SELECT_START_YEAR_LABEL']">
				<dz-select [label]="'Select Start Year' | dcr : HYPO_SELECT_START_YEAR_LABEL" [(value)]="startYearValue" (valueChange)="onStartYearChange()">
					<dz-option *ngFor="let year of startYearOptions" [value]="year">{{ year }}</dz-option>
				</dz-select>
			</div>
		</ng-container>
		<div class="col-4 btn-container">
			<button dz-button color="secondary" dcrHTMLKey="HYPOTHETICAL_GROWTH_BUTTON" (click)="updateHypotheticalGrowth()">Apply</button>
		</div>
		<div *ngIf="isHypoGrossPerfChartValid && isCustomFeeEnabled" class="col-16">
			<cfe-custom-advisory-fee-dialog-button [customAdvisoryFee]="customAdvisoryFee"></cfe-custom-advisory-fee-dialog-button>
		</div>
	</div>
	<div class="row error-row">
		<div class="col-24">
			<div class="error-msg-wrapper" *ngIf="isInvalidInput">
				<icon type="exclamation-triangle"></icon> <span class="error text-12" dcrHTMLKey="HYPOTHETICAL_AMOUNT_ERROR">Please enter a valid amount.</span>
			</div>
			<div class="error-msg-wrapper" *ngIf="isMonthUnselected && !isInvalidInput">
				<icon type="exclamation-triangle"></icon> <span class="error text-12" dcrHTMLKey="HYPOTHETICAL_MONTH_SELECTION_ERROR">Please select a start month.</span>
			</div>
		</div>
	</div>
</ng-container>

<section *ngIf="shouldShowChart">
	<bfa-chart
		id="pa-perf-overview-chart"
		[name]="chartName"
		[data]="combinedPerfChartTabData"
		[type]="'line'"
		[height]="500"
		tooltipType="currency-and-percentage"
		[customAttributes]="customChartAttributes"
	></bfa-chart>
	<perf-performance-chart-grouped-legend
		*ngIf="isHypoGrossPerfChartValid && isCustomFeeEnabled"
		[isTableDataPerPortfolio]="true"
		[chartData]="combinedPerfChartTabData"
		[seriesColors]="groupedLegendColours"
		[groupingConfiguration]="groupedLegendColumnConfiguration"
		[seriesLabelIcon]="seriesIcon.DASH"
	></perf-performance-chart-grouped-legend>
</section>
<br />

<!-- Disclaimer if table or chart are valid-->
<section *ngIf="(isHypoGrossPerfValidData || shouldShowChart) && !showSimplifiedDisclaimer">
	<ng-container *ngTemplateOutlet="disclaimers"></ng-container>
</section>

<!-- Gross performance simplified disclaimer if table or chart are valid -->
<section *ngIf="(isHypoGrossPerfValidData || isHypoGrossPerfChartValid) && showSimplifiedDisclaimer">
	<ng-container *ngTemplateOutlet="disclaimerSimplified"></ng-container>
</section>

<!-- Simplified disclaimers for hypo chart-->
<ng-template #disclaimerSimplified>
	<ac-disclosure [isExpandable]="isDisclosureExpandable">
		<section dcrHTMLKey="HYPO_PERFORMANCE_DISCLAIMER" data-cy="hypothetical-performance-disclosure">HYPO_PERFORMANCE_DISCLAIMER</section>
		<section>
			<section dcrHTMLKey="PERFORMANCE_SOURCE">Source: Morningstar, Refinitive and BlackRock Aladdin</section>
			<section data-cy="hypothetical-performance-source-as-of-date">
				<span dcrHTMLKey="AS_OF_DATE">As of: </span>{{ asOfDate }} <span dcrHTMLKey="CURRENCY"> Currency: </span>{{ displayISOCurrency
				}}<span dcrHTMLKey="PERFORMANCE_POST_DISCLOSURE_TEXT">, performance is net of fees</span>
			</section>
		</section>
	</ac-disclosure>
</ng-template>

<ng-template #disclaimers>
	<ac-disclosure [isExpandable]="isDisclosureExpandable">
		<!-- Disclaimers gross data -->
		<ng-container>
			<ac-tpm-disclosure></ac-tpm-disclosure>
			<section *ngIf="isCustomFeeEnabled && !customAdvisoryFee" dcrHTMLKey="NO_NET_PERF_DISCLOSURE">NO_NET_PERF_DISCLOSURE</section>
			<section *ngIf="hasModel && hasUserPortfolio" dcrHTMLKey="HYPO_DISCLAIMER_BLK_VS_CUSTOM">HYPO_DISCLAIMER_BLK_VS_CUSTOM</section>
			<section *ngIf="hasModel && !hasUserPortfolio && data.portfolioData.length > 1" dcrHTMLKey="HYPO_DISCLAIMER_BLK_VS_BLK">HYPO_DISCLAIMER_BLK_VS_BLK</section>
			<section *ngIf="!hasModel && hasUserPortfolio && data.portfolioData.length > 1" dcrHTMLKey="HYPO_DISCLAIMER_CUSTOM_VS_CUSTOM">HYPO_DISCLAIMER_CUSTOM_VS_CUSTOM</section>
			<section *ngIf="hasModel && data.portfolioData.length === 1" dcrHTMLKey="HYPO_DISCLAIMER_BLK">HYPO_DISCLAIMER_BLK</section>
			<section *ngIf="hasUserPortfolio && data.portfolioData.length === 1" dcrHTMLKey="HYPO_DISCLAIMER_CUSTOM">HYPO_DISCLAIMER_CUSTOM</section>
		</ng-container>

		<!-- Disclaimer for net data -->
		<ng-container *ngIf="isCustomFeeEnabled">
			<section dcrHTMLKey="NET_PERFORMANCE_HYPO_DISCLAIMER">NET_PERFORMANCE_HYPO_DISCLAIMER</section>
		</ng-container>
	</ac-disclosure>
</ng-template>
<ng-template #annualisedReturnDisclosure> </ng-template>
